import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { DonutDetailsChartProps } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Status } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { colorFromStatus } from '~/util/conversion';
import { ERROR_THRESHOLD, SUCCESS_THRESHOLD, lastYearDateRange } from '~/util/cube';

export interface UseDeliveryReliabilityResult {
  title: string;
  subTitle: string;
  timelinessProps: null | DonutDetailsChartProps;
  isLoading: boolean;
}

export const useDeliveryReliability = (): UseDeliveryReliabilityResult => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DeliveryReliabilityWidget' });

  const cube = 'DeliveryDate';

  const filters: Filter[] = [
    {
      member: `${cube}.supplier_id`,
      operator: 'equals',
      values: [entityId],
    },
  ];

  const deliveryDateSeries = useCubeQuery({
    dimensions: [
      `${cube}.amount_purchase_order_items_month`,
      `${cube}.monthly_purchase_order_items_delivered_on_time`,
      `${cube}.avg_supplier_reliability`,
    ],
    timeDimensions: [
      {
        dimension: `${cube}.date`,
        granularity: 'month',
        dateRange: lastYearDateRange,
      },
    ],
    filters,
  });

  const segments: any[] | undefined = deliveryDateSeries.resultSet?.rawData()?.map((segment) => ({
    onTime: Number(segment[`${cube}.monthly_purchase_order_items_delivered_on_time`]) || 0,
    total: Number(segment[`${cube}.amount_purchase_order_items_month`]) || 0,
    reliability: Number(segment[`${cube}.avg_supplier_reliability`]) || 0,
  }));

  const onTimeCount = segments?.reduce((p, c) => p + c.onTime, 0);
  const totalCount = segments?.reduce((p, c) => p + c.total, 0);
  const delayedCount = totalCount - onTimeCount;
  const averageReliability = segments && segments.reduce((p, c) => p + c.reliability, 0) / segments.length / 100;

  let status = Status.NEUTRAL;
  if (averageReliability) {
    if (averageReliability <= ERROR_THRESHOLD) {
      status = Status.ERROR;
    } else if (averageReliability > ERROR_THRESHOLD && averageReliability <= SUCCESS_THRESHOLD) {
      status = Status.WARNING;
    } else {
      status = Status.SUCCESS;
    }
  }

  const chartData = useMemo(() => {
    return {
      timelinessProps:
        segments && segments.length && totalCount
          ? {
              title: t('timeliness.title'),
              color: colorFromStatus(status),
              dotColor: colorFromStatus(status),
              data: [
                {
                  label: t('timeliness.onTime'),
                  value: onTimeCount,
                  total: totalCount,
                },
                {
                  label: t('timeliness.delayed'),
                  value: delayedCount,
                  total: totalCount,
                },
              ],
            }
          : null,
    };
  }, [segments, t, status, onTimeCount, totalCount, delayedCount]);

  return {
    title: t('title'),
    subTitle: t('subTitle'),
    isLoading: deliveryDateSeries.isLoading,
    ...chartData,
  };
};
